/**
 * Read helpers for the training log <-> session link model.
 *
 * `trainingLogs.linkedSessionIDs: string[]` is the source of truth (element 0
 * is the primary session). `trainingLogs.linkedSessionID: string` is the
 * legacy scalar kept equal to `linkedSessionIDs[0]`. Every reader goes through
 * these helpers so logs written by old clients (scalar only) and new clients
 * (array + scalar) resolve the same way.
 */

/** Minimal shape of a training log for link purposes. */
export interface SessionLinkFields {
  linkedSessionID?: string | null;
  linkedSessionIDs?: string[] | null;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

/**
 * Read rule shared by all clients:
 * `linkedSessionIDs ?? (linkedSessionID ? [linkedSessionID] : [])`.
 * Always returns a new array; never `undefined`.
 */
export const getLinkedSessionIDs = (
  log: SessionLinkFields | null | undefined,
): string[] => {
  if (!log) return [];
  if (Array.isArray(log.linkedSessionIDs)) {
    return log.linkedSessionIDs.filter(isNonEmptyString);
  }
  return isNonEmptyString(log.linkedSessionID) ? [log.linkedSessionID] : [];
};

/**
 * The primary session (`linkedSessionIDs[0]`), or `undefined` when the log has
 * no linked session. Use where exactly one session is needed (training type,
 * share cards, auto-post `sessionID`).
 */
export const getPrimarySessionID = (
  log: SessionLinkFields | null | undefined,
): string | undefined => getLinkedSessionIDs(log)[0];
